import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { storeApi, loadCart, saveCart, loadCode, saveCode } from "./storeApi";

// One store state for every /merch page: the shop's config, the live catalog
// and the cart. The cart is checked against the catalog on every load, so a
// line that sold out (or vanished) is shown as such instead of silently
// disappearing, and a quantity above what's left is lowered with a note.

const StoreContext = createContext(null);
export const useStore = () => useContext(StoreContext);

const snapshot = (product, variant) => ({
  slug: product.slug,
  title: product.title,
  options: variant.optionValues || {},
  image: (product.images.find((i) => i.variantId === variant.id) || product.images[0])?.url || null,
  priceMinor: variant.priceMinor,
});

export function StoreProvider({ config, children }) {
  const [catalog, setCatalog] = useState(null);
  const [error, setError] = useState(null);
  const [cart, setCart] = useState(loadCart);
  const [adjusted, setAdjusted] = useState({});
  const [code, setCodeState] = useState(loadCode);
  const [toast, setToast] = useState(null);
  const loadedAt = useRef(0);
  const inflight = useRef(null);

  // Several countdowns can hit zero together: one request serves them all.
  const refresh = useCallback(() => {
    if (!inflight.current) {
      inflight.current = storeApi
        .catalog()
        .then((c) => {
          loadedAt.current = Date.now();
          setCatalog(c);
          setError(null);
          return c;
        })
        .catch((e) => setError(e.message))
        .finally(() => {
          inflight.current = null;
        });
    }
    return inflight.current;
  }, []);

  useEffect(() => {
    refresh();
    // Coming back to the tab after a while: stock may have moved.
    const onFocus = () => Date.now() - loadedAt.current > 60000 && refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  useEffect(() => saveCart(cart), [cart]);

  const variants = useMemo(() => {
    const map = new Map();
    for (const p of catalog?.products || []) for (const v of p.variants) map.set(v.id, { variant: v, product: p });
    return map;
  }, [catalog]);

  // Reconcile the saved cart with what can be sold right now.
  useEffect(() => {
    if (!catalog) return;
    setCart((c) => {
      let changed = false;
      const notes = {};
      const next = c.map((l) => {
        const hit = variants.get(l.id);
        if (!hit || hit.product.teased) return l;
        const max = hit.variant.maxQty ?? 0;
        const snap = snapshot(hit.product, hit.variant);
        const fresh = JSON.stringify(snap) !== JSON.stringify(l.snap);
        if (max > 0 && l.qty > max) {
          changed = true;
          notes[l.id] = { from: l.qty, to: max };
          return { ...l, qty: max, snap };
        }
        if (fresh) {
          changed = true;
          return { ...l, snap };
        }
        return l;
      });
      if (Object.keys(notes).length) setAdjusted((a) => ({ ...a, ...notes }));
      return changed ? next : c;
    });
  }, [catalog, variants]);

  const lines = useMemo(
    () =>
      cart.map((l) => {
        const hit = variants.get(l.id);
        let state = "ok";
        if (!catalog) state = "loading";
        else if (!hit || hit.product.teased) state = "unavailable";
        else if ((hit.variant.maxQty ?? 0) <= 0) state = hit.variant.reserved ? "reserved" : "soldout";
        const snap = l.snap || {};
        return {
          id: l.id,
          qty: l.qty,
          state,
          variant: hit?.variant || null,
          product: hit?.product || null,
          max: hit?.variant.maxQty ?? 0,
          title: hit?.product.title || snap.title || "Item",
          options: hit?.variant.optionValues || snap.options || {},
          image: snap.image || hit?.product.images[0]?.url || null,
          slug: hit?.product.slug || snap.slug || null,
          priceMinor: hit?.variant.priceMinor ?? snap.priceMinor ?? null,
          // Price moved since it went into the cart (a sale started or ended).
          priceWas: l.seen != null && hit && l.seen !== hit.variant.priceMinor ? l.seen : null,
          adjusted: adjusted[l.id] || null,
        };
      }),
    [cart, variants, catalog, adjusted]
  );

  const count = cart.reduce((s, l) => s + l.qty, 0);
  const sellable = lines.filter((l) => l.state === "ok");
  const blocked = lines.filter((l) => l.state !== "ok" && l.state !== "loading");

  const add = useCallback((product, variant, qty = 1) => {
    const max = variant.maxQty ?? 0;
    setCart((c) => {
      const hit = c.find((l) => l.id === variant.id);
      const next = Math.min(max, (hit?.qty || 0) + qty);
      if (next <= (hit?.qty || 0)) return c;
      const line = { id: variant.id, qty: next, snap: snapshot(product, variant), seen: variant.priceMinor };
      return hit ? c.map((l) => (l.id === variant.id ? line : l)) : [...c, line];
    });
    setToast({ key: Date.now(), product, variant });
  }, []);

  const setQty = useCallback((id, qty) => {
    setCart((c) => (qty <= 0 ? c.filter((l) => l.id !== id) : c.map((l) => (l.id === id ? { ...l, qty } : l))));
    setAdjusted((a) => {
      if (!a[id]) return a;
      const { [id]: _, ...rest } = a;
      return rest;
    });
  }, []);
  const remove = useCallback((id) => setQty(id, 0), [setQty]);
  const removeBlocked = useCallback(() => {
    const ids = new Set(blocked.map((l) => l.id));
    setCart((c) => c.filter((l) => !ids.has(l.id)));
  }, [blocked]);
  const clear = useCallback(() => {
    setCart([]);
    setAdjusted({});
  }, []);
  const setCode = useCallback((v) => {
    setCodeState(v);
    saveCode(v);
  }, []);

  const value = {
    config,
    catalog,
    products: catalog?.products || [],
    collections: catalog?.collections || [],
    sales: catalog?.sales || [],
    error,
    refresh,
    lines,
    sellable,
    blocked,
    count,
    add,
    setQty,
    remove,
    removeBlocked,
    clear,
    code,
    setCode,
    toast,
    dismissToast: () => setToast(null),
  };
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

// Delivery facts for trust lines: cheapest option at home, free-over threshold.
export function useDeliveryFacts() {
  const { config } = useStore();
  const home = (config.countries || []).find((c) => c.code === "CZ") || config.countries?.[0];
  const prices = (home?.methods || []).map((m) => m.priceMinor).filter((n) => n != null);
  return {
    fromMinor: prices.length ? Math.min(...prices) : null,
    freeOverMinor: home?.freeOverMinor ?? config.freeShippingOverMinor ?? null,
    countries: (config.countries || []).length,
    returnDays: config.returnWindowDays || 14,
    card: !!config.cardPayments,
  };
}
